import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getB2bikeDetail, type B2bikeDetail } from "../lib/api.js";

export function B2bikeDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<B2bikeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    getB2bikeDetail(id)
      .then((d) => alive && setData(d))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [id]);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-sm text-zinc-500">Načítám…</div>;
  }

  const largeImages = data.images.filter((i) => i.size === "large");
  const images = largeImages.length > 0 ? largeImages : data.images;
  const primary = images[selectedImage] ?? images[0];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/b2bike"
        className="text-sm text-zinc-500 hover:text-ktm-orange inline-flex items-center gap-1 mb-4"
      >
        ← Zpět na katalog B2Bike
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Galerie */}
        <div>
          <div className="aspect-square bg-zinc-50 rounded-xl ring-1 ring-zinc-200 overflow-hidden flex items-center justify-center">
            {primary ? (
              <img src={primary.url} alt={data.nameCz ?? data.code} className="max-w-full max-h-full object-contain" />
            ) : (
              <div className="text-zinc-300 text-sm">bez obrázku</div>
            )}
          </div>
          {/* ⚠ Šest sloupců dá na mobilu 48px dlaždici — nepoznáš, co na ní je. */}
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setSelectedImage(idx)}
                  className={`aspect-square rounded ring-2 overflow-hidden ${
                    idx === selectedImage ? "ring-ktm-orange" : "ring-zinc-200 hover:ring-zinc-300"
                  }`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {data.producer.name && (
            <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1">
              {data.producer.name}
            </div>
          )}
          <h1 className="text-2xl font-semibold text-zinc-900 mb-1">
            {data.nameCz ?? data.nameEn ?? data.code}
          </h1>
          <div className="text-xs text-zinc-400 font-mono mb-4">
            {data.code} · B2Bike ID {data.b2bikeId}
          </div>

          {data.priceGross && (
            <div className="flex items-baseline gap-3 mb-4">
              <div className="text-3xl font-bold text-zinc-900">
                {Number(data.priceGross).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })}{" "}
                {data.currency}
              </div>
              {data.priceNet && (
                <div className="text-sm text-zinc-500">
                  {Number(data.priceNet).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })} bez DPH
                </div>
              )}
            </div>
          )}

          {data.shortDescCz && (
            <p className="text-sm text-zinc-700 mb-4">{data.shortDescCz}</p>
          )}

          {/* Varianty */}
          {data.variants.length > 0 && (
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">
                Varianty
              </div>
              <div className="space-y-1.5">
                {data.variants.map((v) => (
                  <div
                    key={v.id}
                    className={`flex items-center justify-between gap-3 p-2.5 rounded-lg ring-1 ${
                      v.inStock ? "ring-zinc-200 bg-white" : "ring-zinc-100 bg-zinc-50 opacity-70"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="font-medium text-zinc-900 w-20 truncate">{v.sizeName}</div>
                      {v.ean && (
                        <div className="text-xs text-zinc-400 font-mono truncate">{v.ean}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {v.priceGross && (
                        <div className="text-sm font-medium text-zinc-900">
                          {Number(v.priceGross).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })}
                        </div>
                      )}
                      {v.inStock ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          {v.stockQuantity === -1 ? "∞" : v.stockQuantity ?? "—"}
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
                          ∅
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-zinc-400 space-y-0.5">
            {data.category.name && <div>Kategorie: {data.category.name}</div>}
            {data.vatRate && <div>DPH: {data.vatRate}%</div>}
            {data.cardUrl && (
              <div>
                Karta u dodavatele:{" "}
                <a href={data.cardUrl} target="_blank" rel="noreferrer" className="text-ktm-orange hover:underline">
                  otevřít
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dlouhý popis */}
      {data.longDescCz && (
        <div className="mt-8 prose prose-sm max-w-none">
          <h2 className="text-lg font-semibold text-zinc-900 mb-3">Popis</h2>
          <div
            className="text-sm text-zinc-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: data.longDescCz }}
          />
        </div>
      )}
    </div>
  );
}
